/**
 * Asignación de conglomerados a encuestadores.
 *
 * El tablero evita el error que rompería el diseño: asignar el mismo
 * conglomerado a dos personas. §5.1 fija una toma casi fija de 17–21
 * entrevistas por conglomerado, y es esa toma la que hace autoponderada la
 * muestra dentro del estrato. Duplicar un conglomerado duplicaría su toma.
 */

import { assessDwellingCoverage } from '@capi/shared';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import { useAdmin } from '../store';
import { BarChart, StatusChip, Tile, fmtInt, fmtPct } from '../components/charts';

export function AssignmentsPage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ kind: 'good' | 'critical'; text: string } | null>(null);

  const clusters = useQuery({ queryKey: ['monitoring', 'clusters'], queryFn: api.monitoringClusters });
  const team = useQuery({ queryKey: ['users', 'field-team'], queryFn: () => api.fieldTeam() });
  const instrument = useQuery({ queryKey: ['instrument', 'panel'], queryFn: api.instrumentPanel });

  const assign = useMutation({
    mutationFn: ({ clusterId, encuestadorId }: { clusterId: string; encuestadorId: string }) =>
      api.createAssignment({
        waveId: instrument.data!.wave.id,
        clusterId,
        encuestadorId,
      }),
    onSuccess: () => {
      setMessage({ kind: 'good', text: 'Conglomerado asignado.' });
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      void queryClient.invalidateQueries({ queryKey: ['users', 'field-team'] });
    },
    onError: (e) =>
      setMessage({
        kind: 'critical',
        text: e instanceof ApiError ? e.message : 'No se pudo asignar.',
      }),
  });

  const drawAll = useMutation({
    mutationFn: api.drawAllManzanas,
    onSuccess: (result) => {
      setMessage({
        kind: result.failed > 0 ? 'critical' : 'good',
        text: `Sorteo de manzanas: ${result.drawn} realizados, ${result.skipped} omitidos por tener ya sorteo, ${result.failed} fallidos.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      void queryClient.invalidateQueries({ queryKey: ['frame'] });
    },
    onError: (e) =>
      setMessage({
        kind: 'critical',
        text: e instanceof ApiError ? e.message : 'No se pudo sortear.',
      }),
  });

  const unassigned = clusters.data?.filter((c) => c.assignments.length === 0) ?? [];
  const withoutManzanas = clusters.data?.filter((c) => c.manzanasSelected === 0) ?? [];
  const withoutDwellings = clusters.data?.filter((c) => c.estimatedDwellings === null) ?? [];

  /**
   * Conglomerados donde las viviendas no alcanzan para la cuota.
   *
   * Es distinto de no tener el dato: aquí el dato existe y dice que no se
   * puede. Con menos de una vivienda por entrevista es imposible aunque se
   * visiten todas; entre una y dos alcanza en teoría pero sin margen para
   * rechazos ni ausencias, y §5.3 ya cuenta con tres intentos por vivienda.
   */
  /**
   * Reasignación en curso.
   *
   * Se pide motivo antes de ejecutar porque la reasignación mueve trabajo ya
   * empezado: lo levantado se queda con quien lo hizo y al destino le pasa
   * sólo lo que falta. Sin motivo, la bitácora registra que el conglomerado
   * cambió de manos pero no por qué, que es lo único que sirve cuando hay que
   * explicar una desviación de carga al cerrar el campo.
   */
  const [reasignando, setReasignando] = useState<{
    assignmentId: string;
    clusterId: string;
    encuestadorId: string;
    nombre: string;
  } | null>(null);
  const [motivo, setMotivo] = useState('');

  /**
   * Recalcular no es resortear.
   *
   * Se separa a propósito del botón de sorteo: el sorteo decide QUÉ manzanas
   * le tocan a cada conglomerado y rehacerlo con asignaciones ya repartidas
   * movería el terreno bajo los pies del encuestador. Esto sólo actualiza los
   * números sobre las manzanas que ya salieron.
   */
  const recalcular = useMutation({
    mutationFn: api.recalculateDwellings,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clusters'] });
      void queryClient.invalidateQueries({ queryKey: ['assignments'] });
    },
  });

  const reassign = useMutation({
    mutationFn: ({ id, encuestadorId, reason }: { id: string; encuestadorId: string; reason: string }) =>
      api.reassign(id, encuestadorId, reason),
    onSuccess: () => {
      setReasignando(null);
      setMotivo('');
      void queryClient.invalidateQueries({ queryKey: ['clusters'] });
      void queryClient.invalidateQueries({ queryKey: ['assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['users', 'field-team'] });
    },
  });

  const coberturaCorta = (clusters.data ?? [])
    .map((c) => ({ c, cov: assessDwellingCoverage(c.estimatedDwellings, c.target) }))
    .filter((x) => x.cov !== null && (x.cov.impossible || x.cov.tight));
  const imposibles = coberturaCorta.filter((x) => x.cov!.impossible);
  const activeInterviewers = team.data?.filter((t) => t.assignments.length > 0).length ?? 0;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Asignaciones</h1>
        <p className="page-sub">
          41 conglomerados en 29 secciones, que concentran el 82% del padrón. §9.1 contempla 12
          encuestadores por 12 días efectivos.
        </p>
      </div>

      {message ? (
        <div className={`callout callout--${message.kind}`}>{message.text}</div>
      ) : null}

      <div className="grid grid--4">
        <Tile
          label="Conglomerados asignados"
          value={`${fmtInt((clusters.data?.length ?? 0) - unassigned.length)} / ${fmtInt(clusters.data?.length ?? 0)}`}
        />
        <Tile label="Encuestadores con carga" value={fmtInt(activeInterviewers)} meta="§9.1 contempla 12" />
        <Tile
          label="Sin manzanas sorteadas"
          reference="§5.3.1"
          value={fmtInt(withoutManzanas.length)}
        />
        <Tile
          label="Sin viviendas estimadas"
          reference="§5.3.2"
          value={fmtInt(withoutDwellings.length)}
          meta="Sin ellas no hay salto k"
        />
      </div>

      {reasignando ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">Reasignar conglomerado {reasignando.clusterId}</h2>
              <p className="panel__note">
                Pasa a <strong>{reasignando.nombre}</strong>. Las entrevistas ya levantadas se
                quedan con quien las hizo y al destino sólo le toca lo que falta, de modo que la
                toma del conglomerado no se excede.
              </p>
            </div>
          </div>
          <label className="field">
            <span className="field__label">Motivo</span>
            <input
              className="input"
              value={motivo}
              autoFocus
              placeholder="Baja del encuestador, redistribución de carga, error de asignación…"
              onChange={(e) => setMotivo(e.target.value)}
            />
            <span className="field__hint">
              Queda en la bitácora. Es lo único que explica la desviación de carga al cerrar campo.
            </span>
          </label>
          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={motivo.trim().length < 4 || reassign.isPending}
              onClick={() =>
                reassign.mutate({
                  id: reasignando.assignmentId,
                  encuestadorId: reasignando.encuestadorId,
                  reason: motivo.trim(),
                })
              }
            >
              {reassign.isPending ? 'Reasignando…' : 'Confirmar'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setReasignando(null);
                setMotivo('');
              }}
            >
              Cancelar
            </button>
          </div>
          {reassign.isError ? (
            <div className="callout callout--danger" style={{ marginTop: 12, marginBottom: 0 }}>
              {reassign.error instanceof ApiError
                ? reassign.error.message
                : 'No se pudo reasignar.'}
            </div>
          ) : null}
        </div>
      ) : null}

      {coberturaCorta.length > 0 ? (
        <div className={`callout callout--${imposibles.length > 0 ? 'danger' : 'warning'}`}>
          <span className="callout__title">
            <StatusChip status={imposibles.length > 0 ? 'critical' : 'warning'}>
              {imposibles.length > 0
                ? `${imposibles.length} conglomerado(s) sin viviendas suficientes`
                : `${coberturaCorta.length} conglomerado(s) con cobertura justa`}
            </StatusChip>
          </span>
          {imposibles.length > 0 ? (
            <>
              Las 3 manzanas sorteadas no alcanzan para la cuota ni visitando todas las viviendas.
              Sortea manzanas adicionales en esos conglomerados antes de salir a campo.{' '}
            </>
          ) : null}
          {coberturaCorta
            .slice(0, 8)
            .map((x) => `${x.c.id}: ${x.c.estimatedDwellings} viviendas / ${x.c.target} entrevistas`)
            .join(' · ')}
          {coberturaCorta.length > 8 ? ` · y ${coberturaCorta.length - 8} más` : ''}
        </div>
      ) : null}

      {withoutDwellings.length > 0 ? (
        <div className="callout callout--warning">
          <span className="callout__title">
            <StatusChip status="warning">
              {withoutDwellings.length} conglomerado(s) sin salto sistemático
            </StatusChip>
          </span>
          El encuestador no puede ejecutar el recorrido de §5.3.2 sin k. Falta el número de
          viviendas por manzana: la cartografía del INE da el polígono pero no las viviendas, que
          son un dato censal. Se pueden estimar por la superficie de cada manzana, registrarlas a
          mano en Marco muestral, o cargar una capa del censo del INEGI que las traiga.
          {can('assignments:write') ? (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => recalcular.mutate()}
                disabled={recalcular.isPending}
              >
                {recalcular.isPending ? 'Calculando…' : 'Estimar por superficie'}
              </button>
              <span className="faint" style={{ marginLeft: 8 }}>
                No vuelve a sortear: sólo recalcula sobre las manzanas que ya salieron.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {withoutManzanas.length > 0 && can('assignments:write') ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Sorteo de manzanas de arranque <span className="ref">§5.3.1</span>
              </h2>
              <p className="panel__note">
                3 manzanas por conglomerado, con la semilla PPS del documento para que el sorteo sea
                reproducible y auditable. Las manzanas de conglomerados que comparten unidad se
                sortean sin repetir: es lo que evita que los 3 conglomerados de la 722 ordinaria
                caigan en las mismas manzanas.
              </p>
            </div>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => drawAll.mutate()}
              disabled={drawAll.isPending}
            >
              {drawAll.isPending ? 'Sorteando…' : `Sortear ${withoutManzanas.length} pendientes`}
            </button>
          </div>

          {drawAll.data && drawAll.data.failed > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Conglomerado</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {drawAll.data.results
                    .filter((r) => r.status === 'error')
                    .map((result) => (
                      <tr key={result.clusterId}>
                        <td className="strong">{result.clusterId}</td>
                        <td className="faint">{result.detail}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* -------------------------- Carga por persona ------------------------ */}

      {team.data && team.data.length > 0 ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">Carga por encuestador</h2>
              <p className="panel__note">
                Incluye la regla del método de cumpleaños que le corresponde a cada uno (§5.3.3):
                alternar entre «próximo» y «último» reparte el sesgo de la selección dentro de la
                vivienda.
              </p>
            </div>
          </div>

          <BarChart
            showTargetLegend
            rows={team.data.map((member) => ({
              key: member.id,
              label: `${member.code} · ${member.name.split(' ')[0]}`,
              value: member.totals.capturedInterviews,
              target: member.totals.targetInterviews,
              valueLabel: `${fmtInt(member.totals.capturedInterviews)} / ${fmtInt(member.totals.targetInterviews)}`,
              tooltip: `${member.name}: ${member.totals.clusters} conglomerado(s), ${fmtInt(member.totals.capturedInterviews)} de ${fmtInt(member.totals.targetInterviews)} entrevistas (${fmtPct(member.totals.progress, 1)}) · cumpleaños ${member.birthdayRule ?? 'sin asignar'}`,
            }))}
          />

          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Encuestador</th>
                  <th>Supervisor</th>
                  <th>Regla de cumpleaños</th>
                  <th>Conglomerados</th>
                  <th>Avance</th>
                </tr>
              </thead>
              <tbody>
                {team.data.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="strong">{member.name}</div>
                      <div className="faint mono">{member.code}</div>
                    </td>
                    <td className="faint">{member.supervisor?.name ?? '—'}</td>
                    <td className="faint">
                      {member.birthdayRule === 'ultimo'
                        ? 'Último cumpleaños'
                        : member.birthdayRule === 'proximo'
                          ? 'Próximo cumpleaños'
                          : '—'}
                    </td>
                    <td>
                      {member.assignments.length === 0 ? (
                        <span className="faint">Sin asignación</span>
                      ) : (
                        <div className="row row--wrap" style={{ gap: 4 }}>
                          {member.assignments.map((assignment) => (
                            <span
                              key={assignment.id}
                              className="chip"
                              title={`Sección ${assignment.cluster.seccion} · ${assignment.cluster.reference}`}
                            >
                              {assignment.cluster.id} · {assignment.capturedInterviews}/
                              {assignment.targetInterviews}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="num strong">{fmtPct(member.totals.progress, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="callout callout--warning">
          <span className="callout__title">No hay encuestadores activos</span>
          Crea las cuentas de campo en Usuarios antes de asignar conglomerados.
        </div>
      )}

      {/* --------------------------- Conglomerados -------------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Conglomerados</h2>
            <p className="panel__note">
              Un conglomerado sólo puede estar asignado a una persona a la vez. Para cambiarlo, usa
              la reasignación: conserva las entrevistas ya levantadas y transfiere sólo las que
              faltan.
            </p>
          </div>
        </div>

        {clusters.data ? (
          <div className="table-wrap scroll-y">
            <table className="table table--numeric">
              <thead>
                <tr>
                  <th>Congl.</th>
                  <th>Sección</th>
                  <th>Est.</th>
                  <th>Meta</th>
                  <th>Levantadas</th>
                  <th>Viviendas</th>
                  <th>Salto</th>
                  <th>Manzanas</th>
                  <th>Asignado a</th>
                  {can('assignments:write') ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {clusters.data.map((cluster) => (
                  <tr key={cluster.id}>
                    <td className="strong">{cluster.id}</td>
                    <td>{cluster.seccion}</td>
                    <td>{cluster.stratumId}</td>
                    <td>{cluster.target}</td>
                    <td className="strong">{cluster.completed}</td>
                    <td className="faint">{cluster.householdsContacted}</td>
                    <td>
                      {(() => {
                        // Cada quien puede tener su propio salto cuando el
                        // conglomerado se reparte: su meta es distinta.
                        const ks = [
                          ...new Set(
                            cluster.assignments
                              .map((a) => a.systematicSkipK)
                              .filter((k): k is number => k !== null),
                          ),
                        ];
                        if (ks.length === 0) return '—';
                        return ks.map((k) => `1/${k}`).join(' · ');
                      })()}
                    </td>
                    <td>
                      {cluster.manzanasSelected > 0 ? (
                        cluster.manzanasSelected
                      ) : (
                        <StatusChip status="warning">—</StatusChip>
                      )}
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      {cluster.assignments.length > 0 ? (
                        cluster.assignments.map((a) => (
                          <div key={a.id} style={{ marginBottom: 4 }}>
                            <span className="strong">{a.encuestador.code}</span>{' '}
                            <span className="faint">
                              {a._count.interviews}/{a.targetInterviews}
                              {cluster.assignments.length > 1
                                ? ` · ${a._count.manzanas} manz.`
                                : ''}
                            </span>
                          </div>
                        ))
                      ) : (
                        <StatusChip status="warning">Sin asignar</StatusChip>
                      )}
                    </td>
                    {can('assignments:write') ? (
                      <td>
                        {team.data && instrument.data ? (
                          <select
                            className="select"
                            style={{ minWidth: 150 }}
                            value=""
                            onChange={(e) => {
                              if (!e.target.value) return;
                              assign.mutate({
                                clusterId: cluster.id,
                                encuestadorId: e.target.value,
                              });
                            }}
                          >
                            <option value="">
                              {cluster.assignments.length === 0
                                ? 'Asignar a…'
                                : 'Añadir encuestador…'}
                            </option>
                            {team.data
                              // Quien ya lo tiene no se vuelve a ofrecer.
                              .filter(
                                (m) =>
                                  !cluster.assignments.some((a) => a.encuestador.id === m.id),
                              )
                              .map((member) => (
                                <option key={member.id} value={member.id}>
                                  {member.code} · {member.name} ({member.totals.clusters})
                                </option>
                              ))}
                          </select>
                        ) : null}

                        {/**
                          * Reasignar es por PERSONA, no por conglomerado.
                          * Con el conglomerado repartido entre varios, un
                          * único selector no diría a quién sustituye.
                          */}
                        {cluster.assignments.map((a) =>
                          team.data ? (
                            <select
                              key={a.id}
                              className="select"
                              style={{ minWidth: 150, marginTop: 4 }}
                              value=""
                              onChange={(e) => {
                                const destino = team.data?.find((m) => m.id === e.target.value);
                                if (!destino) return;
                                setReasignando({
                                  assignmentId: a.id,
                                  clusterId: cluster.id,
                                  encuestadorId: destino.id,
                                  nombre: `${destino.code} · ${destino.name}`,
                                });
                                setMotivo('');
                              }}
                            >
                              <option value="">Sustituir a {a.encuestador.code}…</option>
                              {team.data
                                .filter(
                                  (m) =>
                                    !cluster.assignments.some((x) => x.encuestador.id === m.id),
                                )
                                .map((member) => (
                                  <option key={member.id} value={member.id}>
                                    {member.code} · {member.name} ({member.totals.clusters})
                                  </option>
                                ))}
                            </select>
                          ) : null,
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="spinner" />
        )}
      </div>
    </>
  );
}
